import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useParams } from 'react-router';

import { ApiError } from '@shared/api/client';
import { artworkUrl } from '@shared/assets/artwork';
import { fieldErrorsFrom, hasFieldErrors } from '@shared/api/field-errors';
import { Breadcrumb } from '@shared/chrome/Breadcrumb';
import { resolveErrorCopy } from '@shared/errors/catalog';
import { formatDate, formatDateTime, formatNumber, toMachineDate } from '@shared/format';
import { StateBadge } from '@shared/ui/StateBadge';
import { SurfaceError } from '@shared/ui/SurfaceError';
import { useToast } from '@shared/ui/toast/ToastProvider';

import { ArtworkPurposeTable, CreditEditor, UploadZone } from '../_shared/components';
import { useAddCredit, useRemoveCredit } from '../_shared/credits.mutations';
import { hasSeasons, resolvePublishState } from '../_shared/constants';
import { sharedCopy } from '../_shared/copy';
import {
  EMPTY_TITLE_FORM,
  titleFormSchema,
  toTitleWriteBody,
  type TitleFormValues,
} from '../_shared/forms.schema';
import { useRefetchExternalRatings, useUpdateContentItem, useUploadCover } from '../_shared/mutations';
import {
  adminItemKey,
  requiredArtworkFloor,
  toAcceptAttribute,
  useAdminContentItem,
  useCategories,
  useContentTypeSchema,
  useCreditProfiles,
  useCreditRoles,
  useItemArtworks,
  useTags,
  useUploadPolicy,
  vocabularyOf,
} from '../_shared/queries';
import { TitleFields } from '../_shared/TitleFields';
import { ProviderCard } from './ContentEdit.ProviderCard';
import { ContentEditSkeleton } from './ContentEdit.Skeleton';
import { copy } from './copy';
import { errors } from './errors';

/**
 * `/content/:contentItemId/edit` — the authored title's own fields.
 *
 * **The state badge sits beside the heading and is not repeated in the facts
 * list.** It is the first thing an operator wants on opening a title, and one
 * value in two places contradicts itself at the first edit touching either.
 *
 * **No transition happens here.** Publishing, scheduling, and unpublishing all
 * live on the content list; what stands in their place is a link back to it, so
 * the surface names its own successor instead of dead-ending. The facts card is
 * information, and it says where the transitions are.
 *
 * **No delete on the title, no version history, no public preview, and no
 * comments panel.** Removing assets and seasons lives on the assets surface; the
 * audit log is deferred and no surface reads it; the public composition lives on
 * another host, and a second renderer here would disagree with it unnoticed; and
 * comments are worked in the moderation queue, which has its own route.
 */
export function ContentEditPage() {
  const { contentItemId } = useParams();
  const queryClient = useQueryClient();
  const toast = useToast();

  const itemQuery = useAdminContentItem(contentItemId);
  const categories = useCategories();
  const tags = useTags();
  const typeSchema = useContentTypeSchema();
  const creditRolesQuery = useCreditRoles();
  const profiles = useCreditProfiles();
  const uploadPolicy = useUploadPolicy();
  // The shape and the floor a cover must clear, READ off the published policy.
  // Stated under the upload zone, so the requirement is met before the transfer
  // rather than learned from the refusal that ends it.
  const coverFloor = requiredArtworkFloor(uploadPolicy.data);
  const itemArtworks = useItemArtworks(contentItemId);

  const updateItem = useUpdateContentItem();
  const uploadCover = useUploadCover();
  const refetchRatings = useRefetchExternalRatings();
  const addCredit = useAddCredit();
  const removeCredit = useRemoveCredit();

  const [formError, setFormError] = useState<string | null>(null);
  // What the service refused, by field. Drawn beside the control that caused it;
  // the page-level line is kept for a refusal that belongs to no single field.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [coverError, setCoverError] = useState<string | null>(null);
  // Distinct from `coverError`: the upload was ACCEPTED and the derived cover
  // did not appear inside the wait. Nothing was rejected, so it is not stated
  // as a refusal.
  const [coverNotice, setCoverNotice] = useState<string | null>(null);

  const item = itemQuery.data;
  const heldPurposes = new Set(itemArtworks.data ?? []);
  // READ, not held. The service refuses a role outside its own configured set,
  // so a copy that drifts makes the write impossible rather than the label wrong.
  const creditRoles = creditRolesQuery.data ?? [];

  const form = useForm<TitleFormValues>({
    resolver: zodResolver(titleFormSchema),
    defaultValues: EMPTY_TITLE_FORM,
  });
  const { reset } = form;

  // The form is seeded from the record every time the record changes — a
  // re-fetch that landed new provider values must reach the inputs, because the
  // fill writes into the real fields rather than beside them.
  useEffect(() => {
    if (!item) return;

    reset({
      title: item.title,
      description: item.description,
      categoryId: item.category_id ?? '',
      tagIds: item.tag_ids ?? [],
      productionYear: item.metadata.production_year === null ? '' : String(item.metadata.production_year),
      producingCountry: item.metadata.producing_country ?? '',
      ageRating: item.metadata.age_rating ?? '',
    });
  }, [item, reset]);

  function failureMessage(error: unknown) {
    const status = error instanceof ApiError ? error.status : 0;
    const code = error instanceof ApiError ? error.code : undefined;
    return resolveErrorCopy(code, status, errors).message;
  }

  if (itemQuery.isError) {
    return (
      <>
        <Breadcrumb
          items={[
            { label: sharedCopy.crumbs.console, to: '/' },
            { label: sharedCopy.crumbs.content, to: '/content' },
          ]}
        />
        <div className="mt-6">
          <SurfaceError
            error={itemQuery.error}
            catalog={errors}
            onRetry={() =>
              void queryClient.invalidateQueries({ queryKey: adminItemKey(contentItemId ?? '') })
            }
          />
        </div>
      </>
    );
  }

  if (!item) {
    return (
      <div className="mt-6">
        <ContentEditSkeleton />
      </div>
    );
  }

  const state = resolvePublishState(item.state);
  const series = hasSeasons(item.content_type);

  return (
    <>
      <Breadcrumb
        items={[
          { label: sharedCopy.crumbs.console, to: '/' },
          { label: sharedCopy.crumbs.content, to: '/content' },
          { label: item.title },
        ]}
      />

      <div className="mb-6 mt-3 grid gap-stack-tight">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="mb-0 font-display text-h1 font-bold">{item.title}</h1>
          <StateBadge kind={state.kind}>{state.label}</StateBadge>
        </div>
        <p className="mb-0 max-w-measure text-[1.0625rem] text-ink-muted">
          {series ? copy.head.ledeSeries : copy.head.ledeFilm}
        </p>
      </div>

      <p className="mb-6 max-w-measure text-xs text-ink-muted">{sharedCopy.requiredNote}</p>

      {formError ? (
        <p role="alert" className="mb-6 max-w-measure text-sm text-danger">
          {formError}
        </p>
      ) : null}

      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          setFormError(null);
          setFieldErrors({});

          updateItem.mutate(
            { contentItemId: item.id, body: toTitleWriteBody(form.getValues()) },
            {
              onSuccess: () => toast.show(copy.actions.saved, 'success'),
              onError: (error) => {
                setFieldErrors(fieldErrorsFrom(error));
                // Said once. A refusal that named its fields has already been
                // stated beside each of them.
                setFormError(hasFieldErrors(error) ? null : failureMessage(error));
              },
            },
          );
        }}
        className="grid gap-8 lg:grid-cols-[20rem_minmax(0,1fr)] lg:items-start lg:gap-9"
      >
        {/*
          The rail comes first in the tree because that is the phone reading
          order — a title's state before its editing — and the explicit column
          places it beside the form on the wide screen without an `order` rule
          that would split what is seen from what is heard.
        */}
        <aside aria-label={copy.state.heading} className="grid gap-6">
          <section
            aria-labelledby="edit-state"
            className="grid gap-3 rounded-md border border-border bg-surface p-4"
          >
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <h2 id="edit-state" className="font-display text-h3 font-bold">
                {copy.state.heading}
              </h2>
              <Link
                to="/content"
                className="hit-target ms-auto flex items-center rounded-sm border border-border-strong bg-surface-raised px-3 text-xs font-semibold text-ink no-underline hover:bg-surface-overlay"
              >
                {copy.state.changeFromList}
              </Link>
            </div>

            <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
              {/*
                The hidden badge sits beside the state rather than replacing it:
                the record really is published, and only a published title can
                be hidden this way — a draft is invisible because it is a draft,
                which the state badge already says.
              */}
              <dt className="text-ink-muted">{copy.state.current}</dt>
              <dd className="mb-0 flex flex-wrap items-center gap-2">
                <StateBadge kind={state.kind}>{state.label}</StateBadge>
                {item.state === 'PUBLISHED' && !item.has_playable_asset ? (
                  <StateBadge kind="warning">{copy.state.notVisibleYet}</StateBadge>
                ) : null}
                {/*
                  The mirror of the badge beside it, for the half of the machine
                  that comes before publication: a title with nothing playable is
                  refused by `:publish`, and the operator reads this card before
                  walking to the list to press it.
                */}
                {item.state !== 'PUBLISHED' && !item.has_playable_asset ? (
                  <StateBadge kind="warning">{copy.state.notPublishable}</StateBadge>
                ) : null}
              </dd>

              {item.state === 'PUBLISHED' && !item.has_playable_asset ? (
                <dd className="col-span-2 mb-0 text-xs text-warning">{copy.state.notVisibleYetHint}</dd>
              ) : null}

              {item.state !== 'PUBLISHED' && !item.has_playable_asset ? (
                <dd className="col-span-2 mb-0 text-xs text-warning">{copy.state.notPublishableHint}</dd>
              ) : null}

              <dt className="text-ink-muted">{copy.state.publishedAt}</dt>
              <dd className="mb-0">
                <DateOrDash iso={item.published_at} />
              </dd>

              <dt className="text-ink-muted">{copy.state.updatedAt}</dt>
              <dd className="mb-0">
                <time dateTime={toMachineDate(item.updated_at)}>
                  <bdi>{formatDateTime(item.updated_at)}</bdi>
                </time>
                {item.updated_by_name ? <> · {item.updated_by_name}</> : null}
              </dd>

              {/*
                The identifier is 26 unbroken characters and the value column of
                this two-column list is narrower than that at desktop width, so
                on the shared row it wrapped with a single character orphaned on
                the second line. It takes a row of its own instead: label on the
                first column, value spanning both on the line beneath, where the
                card's full inner width holds it whole.
              */}
              <dt className="text-ink-muted">{copy.state.reference}</dt>
              <dd className="col-span-2 mb-0">
                <bdi className="mono">{item.reference}</bdi>
              </dd>
            </dl>

            <p className="mb-0 max-w-none text-xs text-ink-muted">{copy.state.note}</p>
          </section>

          <ProviderCard
            binding={item.external}
            busy={refetchRatings.isPending}
            onRefetch={() =>
              refetchRatings.mutate(item.id, {
                onSuccess: () => toast.show(copy.provider.refetched, 'success'),
                onError: (error) => toast.show(failureMessage(error), 'danger'),
              })
            }
          />

          <section aria-labelledby="edit-cover" className="grid gap-3">
            <h2 id="edit-cover" className="font-display text-h3 font-bold">
              {copy.cover.heading}
            </h2>

            {artworkUrl(item.cover.url) ? (
              <img
                src={artworkUrl(item.cover.url) ?? undefined}
                alt=""
                width={item.cover.width ?? 140}
                height={item.cover.height ?? 210}
                className="h-auto w-[8rem] rounded-sm border border-border"
              />
            ) : null}

            <UploadZone
              compact
              label={copy.cover.dropLabel}
              accept={toAcceptAttribute(uploadPolicy.data?.accepted_artwork_formats)}
              hint={copy.cover.hint(
                (uploadPolicy.data?.accepted_artwork_formats ?? []).join(' · '),
                coverFloor?.ratio ?? '',
                // A pixel floor is a MACHINE figure on a human line, and the
                // digits follow the surface: this page is Arabic.
                coverFloor
                  ? `${formatNumber(coverFloor.minWidth)}×${formatNumber(coverFloor.minHeight)}`
                  : '',
              )}
              error={coverError}
              onFiles={(files) => {
                const file = files[0];
                if (!file) return;
                setCoverError(null);
                setCoverNotice(null);
                uploadCover.mutate(
                  { contentItemId: item.id, file },
                  {
                    onSuccess: (result) => {
                      setCoverNotice(result.derived ? null : copy.cover.notDerived);
                      toast.show(copy.cover.uploaded, 'success');
                    },
                    // Under the cover control. A rejected image is a rejection of
                    // the image, not of everything else on the page.
                    onError: (error) => setCoverError(failureMessage(error)),
                  },
                );
              }}
            />

            {uploadCover.isPending ? (
              <p role="status" className="mb-0 max-w-none text-xs text-ink-muted">
                {copy.cover.uploading}
              </p>
            ) : null}

            {coverNotice !== null ? (
              <p role="status" className="mb-0 max-w-none text-xs text-warning">
                {coverNotice}
              </p>
            ) : null}

            {/*
              The vocabulary is the upload policy's — purposes and ratios are
              published, never compiled in — and the source column is this
              title's own artwork read. `item.artwork_purposes` is `[]` by
              construction: `content`'s detail payload does not compose them,
              which is why this table used to draw a header over no rows.
            */}
            <ArtworkPurposeTable
              rows={(uploadPolicy.data?.artwork_purposes ?? []).map((purpose) => ({
                purpose: purpose.purpose,
                ratio: purpose.ratio,
                source: heldPurposes.has(purpose.purpose)
                  ? purpose.required
                    ? 'UPLOADED'
                    : 'DERIVED'
                  : 'MISSING',
              }))}
            />

            <p className="mb-0 max-w-none text-xs text-ink-muted">
              {sharedCopy.artwork.note} {copy.cover.perAssetNote}
            </p>
          </section>
        </aside>

        <div className="grid gap-8">
          <TitleFields
            register={form.register}
            control={form.control}
            categories={categories.data?.items ?? []}
            tags={tags.data?.items ?? []}
            ageRatings={vocabularyOf(typeSchema.data, item.content_type, 'age_rating')}
            fieldErrors={fieldErrors}
          />

          <section aria-labelledby="edit-credits" className="grid gap-3">
            <h2 id="edit-credits" className="font-display text-h3 font-bold">
              {copy.credits.heading}
            </h2>
            <p className="mb-0 max-w-measure text-xs text-ink-muted">{copy.credits.lede}</p>

            <CreditEditor
              credits={item.credits}
              roleVocabulary={creditRoles}
              profiles={profiles.data ?? []}
              registryHref={
                <Link to="/people" className="text-accent-text">
                  {sharedCopy.credits.registryLink}
                </Link>
              }
              onToggleRole={(profileId, role) => {
                const credit = item.credits.find((row) => row.credit_profile_id === profileId);
                const held = credit?.roles.find((entry) => entry.role === role);

                // A credit is a record, so the chip toggles one: pressing adds,
                // un-pressing removes the record that holds that role by its id.
                if (held) {
                  removeCredit.mutate({
                    creditProfileId: profileId,
                    creditId: held.credit_id,
                    contentItemId: item.id,
                  });
                  return;
                }

                addCredit.mutate({ creditProfileId: profileId, contentItemId: item.id, role });
              }}
              onRemove={(profileId) => {
                const credit = item.credits.find((row) => row.credit_profile_id === profileId);
                credit?.roles.forEach((entry) =>
                  removeCredit.mutate({
                    creditProfileId: profileId,
                    creditId: entry.credit_id,
                    contentItemId: item.id,
                  }),
                );
              }}
              onAdd={(profileId) => {
                const firstRole = creditRoles[0];
                if (!firstRole) return;
                addCredit.mutate({
                  creditProfileId: profileId,
                  contentItemId: item.id,
                  role: firstRole,
                });
              }}
            />
          </section>

          {/*
            **Full-width stack below `--bp-sm`, one row from it** — the UI/UX
            pack's `[data-form-actions] .btn` rule, whose stated reason is that
            two buttons sharing a line on a 320 px window drop below the 2.75 rem
            touch target. Three of them wrapped two-and-one on a phone, and the
            orphaned third read as a control of a different kind rather than the
            third member of one set.

            The order is the pack's and survives the stack: the commit first, the
            route on to the assets second, the way out last.
          */}
          <div className="grid gap-3 sm:flex sm:flex-wrap">
            <button
              type="submit"
              disabled={updateItem.isPending}
              className="w-full rounded-sm bg-accent px-5 py-3 text-sm font-semibold text-accent-ink hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-45 sm:w-auto"
            >
              {updateItem.isPending ? copy.actions.saving : copy.actions.save}
            </button>

            <Link
              to={`/content/${item.id}/assets`}
              className="hit-target flex w-full items-center justify-center rounded-sm border border-border-strong bg-surface-raised px-5 text-sm font-semibold text-ink no-underline hover:bg-surface-overlay sm:w-auto"
            >
              {series ? copy.actions.toAssets : copy.actions.toAssetsFilm}
            </Link>

            <Link
              to="/content"
              className="hit-target flex w-full items-center justify-center rounded-sm px-5 text-sm text-ink no-underline hover:bg-hover-bg sm:w-auto"
            >
              {copy.actions.back}
            </Link>
          </div>
        </div>
      </form>
    </>
  );
}

/** A date, or the absence of one stated rather than left blank. */
function DateOrDash({ iso }: { iso: string | null }) {
  if (!iso) return <span className="text-ink-muted">{copy.state.never}</span>;

  return (
    <time dateTime={toMachineDate(iso)}>
      <bdi>{formatDate(iso)}</bdi>
    </time>
  );
}
